import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import QuestionCard from "@/components/QuestionCard";
import { KineticText, Reveal } from "@/components/motion/primitives";
import { ChevronLeft, ChevronRight, BookOpen, Loader2 } from "lucide-react";
import { ChapterNav, BackToTop } from "@/components/ChapterNav";
import Seo from "@/components/Seo";
import { LoadError, isNotFound } from "@/components/LoadError";

const chapterId = (n) => `chapter-${n}`;

function BookNav({ prev, next, position, total, bottom = false }) {
  const Item = ({ b, dir }) =>
    b ? (
      <Link
        to={`/book/${encodeURIComponent(b.name)}`}
        data-testid={`book-${dir}-link${bottom ? "-bottom" : ""}`}
        className={`group flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-gold ${dir === "next" ? "flex-row-reverse text-right" : ""}`}
      >
        {dir === "prev" ? <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> : <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
        <span>
          <span className="block font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{dir === "prev" ? "Previous book" : "Next book"}</span>
          <span className="font-serif text-base">{b.name} <span className="font-mono text-xs text-muted-foreground">{b.count}</span></span>
        </span>
      </Link>
    ) : <span />;
  return (
    <div className={`flex items-center justify-between gap-3 ${bottom ? "mt-16 border-t border-border pt-8" : "mb-8"}`} data-testid={`book-nav${bottom ? "-bottom" : ""}`}>
      <Item b={prev} dir="prev" />
      <span className="hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground sm:block">Book {position} of {total}</span>
      <Item b={next} dir="next" />
    </div>
  );
}

export default function Book() {
  const { book } = useParams();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["book", book],
    queryFn: async () => (await api.get(`/books/${encodeURIComponent(book)}`)).data,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError && !isNotFound(error)) return <LoadError onRetry={refetch} />;
  if (isError || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-bold text-foreground">Book not found</h1>
        <p className="mt-3 text-muted-foreground">We don&apos;t have questions catalogued for &ldquo;{book}&rdquo; yet.</p>
        <Link to="/explore/books" className="mt-6 inline-block text-primary hover:underline">Browse all books</Link>
      </div>
    );
  }

  const tone = data.testament === "OT" ? "text-primary" : "text-secondary dark:text-sky-300";

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="book-page">
      <Seo title={`Questions in ${data.book}`} description={`${data.count} questions asked in the book of ${data.book}, chapter by chapter, with word studies and commentary.`} />
      <Link
        to="/explore/books"
        data-testid="book-back-link"
        className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> All books
      </Link>

      <BookNav prev={data.prev} next={data.next} position={data.position} total={data.total_books} />

      <header className="mb-10 border-b border-border pb-8">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] ${tone}`}>
            <BookOpen className="h-4 w-4" />
            {data.testament === "OT" ? "Old Testament" : "New Testament"}
          </span>
          {data.division && (
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">{data.division}</span>
          )}
        </div>
        <KineticText as="h1" text={data.book} className="mt-3 block font-serif text-5xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl" testId="book-title" />
        <p className="mt-3 font-mono text-sm text-muted-foreground" data-testid="book-count">
          {data.count} question{data.count === 1 ? "" : "s"} across {data.chapters.length} chapter{data.chapters.length === 1 ? "" : "s"}
        </p>
      </header>

      <ChapterNav chapters={data.chapters} idFor={chapterId} />

      <div className="space-y-14">
        {data.chapters.map((ch) => (
          <section key={ch.chapter} id={chapterId(ch.chapter)} data-section={ch.chapter} className="scroll-mt-32" data-testid={`chapter-${ch.chapter}`}>
            <div className="mb-6 flex items-center gap-3">
              <span className="font-serif text-3xl font-semibold text-foreground">Chapter {ch.chapter}</span>
              <span className="h-px flex-1 bg-border" />
              <span className="font-mono text-xs text-muted-foreground">{ch.questions.length}</span>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {ch.questions.map((q, i) => (
                <Reveal key={q.id} delay={(i % 2) * 0.08}>
                  <QuestionCard q={q} index={i} />
                </Reveal>
              ))}
            </div>
          </section>
        ))}
      </div>
      <BookNav prev={data.prev} next={data.next} position={data.position} total={data.total_books} bottom />
      <BackToTop />
    </div>
  );
}
